import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { message } from "antd";
import { useTranslation } from "components/use-hooks";
import { GET_EVENT_CHECK_QR_METADATA } from "graphql/query/event-gql";
import { formatCheckInPositionDate } from "../helpers/check-in-position-date-helper";
import { readCheckQrRevision, withCheckQrRevision } from "../helpers/event-check-qr-helper";


const getDateText = (value, key, translations) => (
    translations[ key ].replace(
        "{ date }",
        formatCheckInPositionDate(value) || translations[ "CHECK_QR.Text_DateUnavailable" ]
    )
);

const getQrGeneratedAtText = (value, translations, forExport = false) => {
    const formattedDate = formatCheckInPositionDate(value);

    if (formattedDate)
    {
        return translations[ "CHECK_QR.Text_GeneratedAt" ].replace("{ date }", formattedDate);
    }

    // Legacy QR codes may have no recorded issue date; never substitute location history.
    if (forExport) return null;

    const missingDate = value === null || value === undefined ||
        (typeof value === "string" && !value.trim());

    return missingDate ? translations[ "CHECK_QR.Text_GenerationDateNotRecorded" ] :
        getDateText(value, "CHECK_QR.Text_GeneratedAt", translations);
};

const useCheckQrPosterExport = ({ eventID, qrUrl, qrGeneratedAt, eventTitle, positionDate, disabled = false }) => {
    const client = useApolloClient();
    const translations = useTranslation();
    const [ operation, setOperation ] = useState(null);
    const requestRef = useRef(null);
    const mountedRef = useRef(true);
    const context = useMemo(() => ({ eventID, qrUrl, qrGeneratedAt, disabled, client }),
        [ eventID, qrUrl, qrGeneratedAt, disabled, client ]);
    const activeContextRef = useRef(context);

    activeContextRef.current = context;

    useLayoutEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const exportPoster = async (type, action) => {
        if (requestRef.current?.context === context || disabled || !qrUrl) return false;

        const request = { context, type };
        const isCurrent = () => mountedRef.current && activeContextRef.current === context;
        let cancelled = false;

        requestRef.current = request;
        setOperation(request);

        try
        {
            // The exporter starts synchronously so Print can open its window before fetching.
            const completed = await action(async () => {
                // Fetch the image URL and its issue date together; location history is admin-only.
                const result = await client.query({
                    query: GET_EVENT_CHECK_QR_METADATA,
                    variables: { id: String(eventID) },
                    fetchPolicy: "no-cache",
                    context: { queryDeduplication: false },
                });

                if (!isCurrent())
                {
                    cancelled = true;
                    return null;
                }

                const event = result?.data?.exhibitionEvent;

                if (result?.errors?.length || String(event?.id) !== String(eventID) || !event?.getCheckQrCodeUrl)
                {
                    throw new Error("QR metadata unavailable");
                }

                request.event = event;

                return {
                    url: withCheckQrRevision(event.getCheckQrCodeUrl,
                        event.check_qr_generated_at || readCheckQrRevision(eventID)),
                    eventID,
                    eventTitle,
                    qrGeneratedAtText: getQrGeneratedAtText(event.check_qr_generated_at, translations, true),
                };
            });

            if (completed === false && !cancelled && isCurrent()) throw new Error("QR poster export failed");

            // Publish only after rendering finishes: changing QR props earlier would release the export lock.
            // An obsolete export must never overwrite a newer generation in the shared cache.
            if (completed !== false && isCurrent() && request.event)
            {
                client.writeQuery({
                    query: GET_EVENT_CHECK_QR_METADATA,
                    variables: { id: String(eventID) },
                    data: { exhibitionEvent: request.event },
                });
            }

            return completed;
        } catch (error)
        {
            if (isCurrent()) message.error(translations[ "CHECK_QR.Text_ExportError" ]);

            return false;
        } finally
        {
            if (requestRef.current === request)
            {
                requestRef.current = null;
                if (mountedRef.current) setOperation(null);
            }
        }
    };

    return {
        exportLoading: operation?.context === context ? operation.type : null,
        exportPoster,
        qrGeneratedAtText: getQrGeneratedAtText(qrGeneratedAt, translations),
        positionUpdatedAtText: getDateText(positionDate?.value, "CHECK_QR.Text_PositionUpdatedAt", translations),
    };
};

export default useCheckQrPosterExport;
